import React from 'react';
import { Card, List, Space, Tag, Typography } from 'antd';
import type { QuotationRevisionCompareResult } from '@/components/crm/QuotationRevisionComparePanel';
import { formatMoney, formatPercent, UAE_MONEY_PROFILE } from '@/utils/format';

const { Text } = Typography;

type Props = {
  comparison?: QuotationRevisionCompareResult;
};

export const QuotationApprovalImpactCard: React.FC<Props> = ({ comparison }) => {
  if (!comparison?.commercial_impact) return null;

  const impact = comparison.commercial_impact;
  const currency = impact.currency || 'AED';
  const bullets: string[] = [];

  if (Math.abs(impact.selling_delta) >= 0.005) {
    bullets.push(
      `Sell ${impact.selling_delta < 0 ? 'reduced' : 'increased'} by ${formatMoney(Math.abs(impact.selling_delta), { ...UAE_MONEY_PROFILE, currency })}`
    );
  }
  if (impact.previous_margin_pct != null && impact.revised_margin_pct != null) {
    bullets.push(
      `Margin ${formatPercent(impact.previous_margin_pct, { semantics: 'whole', maximumFractionDigits: 2 })} → ${formatPercent(impact.revised_margin_pct, { semantics: 'whole', maximumFractionDigits: 2 })}`
    );
  }
  if (impact.cost_delta == null || Math.abs(impact.cost_delta) < 0.005) {
    bullets.push('No cost change');
  } else {
    bullets.push(
      `Cost ${impact.cost_delta < 0 ? 'reduced' : 'increased'} by ${formatMoney(Math.abs(impact.cost_delta), { ...UAE_MONEY_PROFILE, currency })}`
    );
  }

  const paymentField = comparison.field_deltas.find((f) =>
    String(f.field).toLowerCase().includes('payment')
  );
  if (paymentField) {
    bullets.push(`Payment terms: ${String(paymentField.previous ?? '—')} → ${String(paymentField.current ?? '—')}`);
  }

  if (bullets.length === 0) return null;

  return (
    <Card size="small" title="Why approval is required" style={{ marginBottom: 16 }}>
      <Space direction="vertical" style={{ width: '100%' }} size="small">
        <div>
          <Tag color="purple">{comparison.classification}</Tag>
          <Tag color="orange">Reapproval: {comparison.reapproval_materiality}</Tag>
        </div>
        <List
          size="small"
          dataSource={bullets}
          renderItem={(item) => (
            <List.Item style={{ padding: '2px 0', border: 'none' }}>
              <Text>• {item}</Text>
            </List.Item>
          )}
        />
      </Space>
    </Card>
  );
};

export default QuotationApprovalImpactCard;
